import React, { useState, useEffect } from "react";

const InvoiceCalculator = () => {
  const [subtotal, setSubtotal] = useState(1000);
  const [taxRate, setTaxRate] = useState(18);
  const [discountType, setDiscountType] = useState("percentage");
  const [discountValue, setDiscountValue] = useState(10);
  const [shippingCost, setShippingCost] = useState(50);
  const [currency, setCurrency] = useState("USD");
  const [additionalCharges, setAdditionalCharges] = useState(0);
  const [result, setResult] = useState({
    subtotalAmount: "0",
    discountAmount: "0",
    taxAmount: "0",
    totalAmount: "0",
    breakdown: "",
  });

  // Invoice Calculation
  const calculateInvoice = () => {
    let sub = parseFloat(subtotal);
    let tax = parseFloat(taxRate);
    let disc = parseFloat(discountValue);
    let ship = parseFloat(shippingCost);
    let extra = parseFloat(additionalCharges);

    if (isNaN(sub) || sub < 0 || isNaN(tax) || tax < 0 || isNaN(disc) || disc < 0 || isNaN(ship) || ship < 0 || isNaN(extra) || extra < 0) return;

    // Calculate discount amount
    let discountAmount = 0;
    if (discountType === "percentage") {
      discountAmount = (sub * disc) / 100;
    } else {
      discountAmount = disc;
    }

    // Calculate amounts after discount
    const amountAfterDiscount = sub - discountAmount;

    // Calculate tax on discounted amount
    const taxAmount = (amountAfterDiscount * tax) / 100;

    // Calculate final total
    const totalAmount = amountAfterDiscount + taxAmount + ship + extra;

    // Format currency symbol
    const currencySymbols = {
      USD: "$",
      EUR: "€",
      GBP: "£",
      INR: "₹",
      JPY: "¥",
      AUD: "A$",
      CAD: "C$",
    };
    const symbol = currencySymbols[currency] || currency;

    const subtotalDisplay = `${symbol}${sub.toFixed(2)}`;
    const discountDisplay = `${symbol}${discountAmount.toFixed(2)}`;
    const taxDisplay = `${symbol}${taxAmount.toFixed(2)}`;
    const totalDisplay = `${symbol}${totalAmount.toFixed(2)}`;

    let breakdown = `Your invoice is calculated as follows: Subtotal (${subtotalDisplay}) minus discount (${discountDisplay}) equals ${symbol}${amountAfterDiscount.toFixed(2)}. Then ${tax}% tax is applied (${taxDisplay}), shipping (${symbol}${ship.toFixed(2)}) and additional charges (${symbol}${extra.toFixed(2)}) are added to reach the final total of ${totalDisplay}.`;

    setResult({
      subtotalAmount: subtotalDisplay,
      discountAmount: discountDisplay,
      taxAmount: taxDisplay,
      totalAmount: totalDisplay,
      breakdown,
    });
  };

  useEffect(() => {
  calculateInvoice();
}, [subtotal, taxRate, discountType, discountValue, shippingCost, currency, additionalCharges]);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "white" }}>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
          </div>
          <h1>Invoice Calculator</h1>
          <p>
            Calculate your invoice totals with precision. Add subtotals, apply taxes and discounts, include shipping costs, and get instant accurate calculations for professional invoicing.
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Invoice Details
              </h2>

              {/* Subtotal Input */}
              <div className="input-group">
                <label>Subtotal ({currency})</label>
                <div className="input-wrapper">
                  <span className="input-prefix">💰</span>
                  <input
                    type="number"
                    value={subtotal}
                    onChange={(e) => setSubtotal(e.target.value)}
                    className="calc-input"
                    placeholder="1000"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="quick-select-group">
                  {[500, 1000, 2000, 5000, 10000].map((val) => (
                    <button
                      key={val}
                      onClick={() => setSubtotal(val)}
                      className="quick-select-btn primary"
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tax Rate Input */}
              <div className="input-group">
                <label>Tax Rate (%)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📊</span>
                  <input
                    type="number"
                    value={taxRate}
                    onChange={(e) => setTaxRate(e.target.value)}
                    className="calc-input"
                    placeholder="18"
                    min="0"
                    max="100"
                    step="0.1"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 5, 10, 15, 18, 20].map((h) => (
                    <button
                      key={h}
                      onClick={() => setTaxRate(h)}
                      className="quick-select-btn success"
                    >
                      {h}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Discount Type */}
              <div className="input-group">
                <label>Discount Type</label>
                <div className="quick-select-group">
                  {[
                    { value: "percentage", label: "Percentage (%)" },
                    { value: "fixed", label: "Fixed Amount" },
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setDiscountType(type.value)}
                      className={`quick-select-btn ${discountType === type.value ? "primary" : "secondary"}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Discount Value */}
              <div className="input-group">
                <label>Discount {discountType === "percentage" ? "(%)" : `(${currency})`}</label>
                <div className="input-wrapper">
                  <span className="input-prefix">🎁</span>
                  <input
                    type="number"
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    className="calc-input"
                    placeholder="10"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Shipping Cost */}
              <div className="input-group">
                <label>Shipping Cost ({currency})</label>
                <div className="input-wrapper">
                  <span className="input-prefix">🚚</span>
                  <input
                    type="number"
                    value={shippingCost}
                    onChange={(e) => setShippingCost(e.target.value)}
                    className="calc-input"
                    placeholder="50"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 25, 50, 100, 150].map((val) => (
                    <button
                      key={val}
                      onClick={() => setShippingCost(val)}
                      className="quick-select-btn success"
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>

              {/* Additional Charges */}
              <div className="input-group">
                <label>Additional Charges ({currency})</label>
                <div className="input-wrapper">
                  <span className="input-prefix">➕</span>
                  <input
                    type="number"
                    value={additionalCharges}
                    onChange={(e) => setAdditionalCharges(e.target.value)}
                    className="calc-input"
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Currency */}
              <div className="input-group">
                <label>Currency</label>
                <div className="quick-select-group">
                  {[
                    { value: "USD", label: "USD ($)" },
                    { value: "EUR", label: "EUR (€)" },
                    { value: "GBP", label: "GBP (£)" },
                    { value: "INR", label: "INR (₹)" },
                  ].map((curr) => (
                    <button
                      key={curr.value}
                      onClick={() => setCurrency(curr.value)}
                      className={`quick-select-btn ${currency === curr.value ? "primary" : "secondary"}`}
                    >
                      {curr.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateInvoice} className="calculate-btn">
                Calculate Invoice Total
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 8v4l3 2"></path>
                </svg>
                Invoice Summary
              </h2>

              <div className="result-card">
                {/* Total Amount */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Total Amount Due</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">{result.totalAmount}</div>
                  <div className="result-subtext">final invoice amount</div>
                </div>

                {/* Subtotal */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Subtotal</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">{result.subtotalAmount}</div>
                  <div className="result-subtext">before tax and fees</div>
                </div>

                {/* Discount */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Discount Applied</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="8 12 12 16 16 12"></polyline>
                        <line x1="12" y1="8" x2="12" y2="16"></line>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">-{result.discountAmount}</div>
                  <div className="result-subtext">savings</div>
                </div>

                {/* Tax Amount */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Tax Amount</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">{result.taxAmount}</div>
                  <div className="result-subtext">tax charges</div>
                </div>

                {/* Breakdown */}
                <div className="breakdown-section" style={{ marginTop: "1rem" }}>
                  <h3 className="breakdown-title">Calculation Breakdown</h3>
                  <p style={{ fontSize: "0.9rem", color: "#065f46" }}>
                    {result.breakdown}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Invoice Calculations</h3>
              <p>
                An invoice is a commercial document that itemizes and records a transaction between a buyer and seller. This calculator helps you accurately compute the final amount by considering all components: subtotal, discounts, taxes, shipping, and additional charges.
                The calculation follows standard accounting practices where discounts are applied before taxes, ensuring accurate and professional invoicing. Use this tool to create transparent, accurate invoices for your business transactions.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Invoice Calculation Method:</h4>
            <div className="formula-display">
              Step 1: Start with the subtotal (base price of goods/services)
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              Step 2: Apply discount (percentage or fixed amount)
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              Step 3: Calculate tax on the discounted amount
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              Step 4: Add shipping costs and additional charges
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Discount Types</div>
                <div className="formula-param-value">% or Fixed Amount</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Tax Calculation</div>
                <div className="formula-param-value">After Discount</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Shipping</div>
                <div className="formula-param-value">Added to Total</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Additional Fees</div>
                <div className="formula-param-value">Processing, Handling, etc.</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>When should I apply discounts?</h5>
              <p>
                Discounts should be applied before calculating taxes. This is the standard practice in most jurisdictions and ensures accurate tax calculation on the actual amount paid.
              </p>
            </div>

            <div className="faq-item">
              <h5>What's the difference between percentage and fixed discounts?</h5>
              <p>
                Percentage discounts are calculated as a percentage of the subtotal (e.g., 10% off $1000 = $100 discount), while fixed discounts are absolute amounts (e.g., $50 off regardless of subtotal).
              </p>
            </div>

            <div className="faq-item">
              <h5>Are shipping costs taxable?</h5>
              <p>
                In this calculator, shipping costs are added after tax calculation. However, tax laws vary by jurisdiction - some places tax shipping, others don't. Adjust accordingly for your region.
              </p>
            </div>

            <div className="faq-item">
              <h5>What are additional charges?</h5>
              <p>
                Additional charges can include processing fees, handling charges, packaging costs, or any other fees that need to be added to the final invoice amount.
              </p>
            </div>

            <div className="faq-item">
              <h5>How do I choose the right currency?</h5>
              <p>
                Select the currency that matches your business transactions or client's preferred currency. The calculator supports major currencies including USD, EUR, GBP, and INR.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceCalculator;